import { motion } from 'framer-motion';
import { Building2, FileText, Briefcase, Shield, Scale, Users, Tractor, FileSearch, BookOpen, ArrowRight } from 'lucide-react';
import { useLanguage, useLp } from '../context/LanguageContext';
import { SEO } from '../components/SEO';
import { Link } from 'react-router-dom';

const ServicesPage = () => {
  const { t } = useLanguage();
  const lp = useLp();

  const services = [
    { key: 'immobiliare', slug: 'immobiliare', icon: Building2 },
    { key: 'successioni', slug: 'successioni', icon: FileText },
    { key: 'aziende', slug: 'aziende', icon: Briefcase },
    { key: 'patrimoniali', slug: 'patrimoniali', icon: Shield },
    { key: 'procure', slug: 'procure', icon: Scale },
    { key: 'consulenza', slug: 'consulenza', icon: Users },
    { key: 'agricola', slug: 'agricola', icon: Tractor },
    { key: 'ipocatastali', slug: 'ipocatastali', icon: FileSearch },
    { key: 'vidimazione', slug: 'vidimazione', icon: BookOpen }
  ];

  return (
    <div data-testid="services-page" className="min-h-screen">
      <SEO page="services" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#121826]" />
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C9A227]/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-4">
              Accordini Carillo
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 max-w-3xl">
              {t('services.title')}
            </h1>
            <p className="text-white/45 text-base md:text-lg max-w-2xl leading-relaxed">
              {t('services.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map(({ key, slug, icon: Icon }, i) => (
              <Link to={lp(`/servizi/${slug}`)} key={key}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  data-testid={`service-card-${key}`}
                  className="group relative bg-white border border-[rgba(30,36,51,0.08)] hover:border-[#C9A227]/40 p-8 transition-all duration-400 shadow-[0_2px_12px_rgba(40,56,128,0.04)] hover:shadow-[0_8px_30px_rgba(40,56,128,0.08)] h-full"
                >
                  <div className="absolute top-0 left-0 w-0 h-[2px] bg-[#C9A227] group-hover:w-full transition-all duration-500" />
                  <div className="w-12 h-12 border border-[#C9A227]/30 flex items-center justify-center mb-6 group-hover:border-[#C9A227] transition-colors">
                    <Icon className="w-6 h-6 text-[#C9A227] stroke-[1.5]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#283880] mb-3 group-hover:text-[#C9A227] transition-colors">
                    {t(`services.items.${key}.title`)}
                  </h3>
                  <p className="text-[#5A6578] text-sm leading-relaxed mb-4">
                    {t(`services.items.${key}.desc`)}
                  </p>
                  <span className="inline-flex items-center text-[#C9A227] text-xs font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {t('ui.services.discover')} <ArrowRight className="ml-1 w-3.5 h-3.5" />
                  </span>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#283880] tracking-tight mb-4">
              {t('hero.cta')}
            </h2>
            <p className="text-[#5A6578] mb-8 max-w-xl mx-auto leading-relaxed">
              {t('about.team')}
            </p>
            <Link to={lp('/contatti')}>
              <button
                data-testid="services-contact-cta"
                className="bg-[#C9A227] text-[#121826] hover:bg-[#B38E1B] h-12 px-10 uppercase tracking-[0.2em] text-[11px] font-bold transition-all duration-300 shadow-[0_4px_20px_rgba(201,162,39,0.2)]"
              >
                {t('nav.contact')}
                <ArrowRight className="inline ml-2 w-4 h-4" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
